import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Dialog,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Iconify from "../components/iconify/Iconify";
import {
  useDeleteTournamentMutation,
  useGetTournamentsQuery,
} from "../features/tournaments/tournamentsApiSlice";
import TournamentFormDialog from "../components/tournaments/TournamentFormDialog";
import TournamentPdfButton from "../components/tournaments/TournamentPdfButton";
import { useDispatch } from "react-redux";
import { setDialog } from "../features/dialog/dialogSlice";

// ----------------------------------------------------------------------

export default function TournamentsPage() {
  const dispatch = useDispatch();
  const {
    data: tournaments = [],
    isLoading,
    isError,
    refetch,
  } = useGetTournamentsQuery();
  const [deleteTournament, { isLoading: isDeleting }] =
    useDeleteTournamentMutation();

  const sortedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments)) return [];
    return [...tournaments].sort((a, b) => {
      const aDate = a?.tournamentDate ? new Date(a.tournamentDate).getTime() : 0;
      const bDate = b?.tournamentDate ? new Date(b.tournamentDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [tournaments]);

  const [openForm, setOpenForm] = useState(false);
  const [editTournament, setEditTournament] = useState(null);

  const handleCreate = () => {
    setEditTournament(null);
    setOpenForm(true);
  };

  const handleEdit = (tournament) => {
    setEditTournament(tournament);
    setOpenForm(true);
  };

  const handleAfterSave = () => {
    setOpenForm(false);
    setEditTournament(null);
    refetch();
  };

  const handleDelete = async (tournament) => {
    const ok = window.confirm(
      `Turnier "${tournament.tournamentName}" wirklich löschen?`
    );
    if (!ok) return;
    try {
      await deleteTournament(tournament._id || tournament.id).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Turnier gelöscht",
          data: {
            successText: `${tournament.tournamentName} wurde gelöscht.`,
          },
        })
      );
      refetch();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Löschen",
          data: { errorText: e?.data?.message || "Löschen fehlgeschlagen." },
        })
      );
    }
  };

  return (
    <>
      <Helmet>
        <title> Dashboard: Turniere </title>
      </Helmet>

      <Container sx={{ pb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4">Turniere</Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreate}
            startIcon={<Iconify icon="mdi:plus" />}
          >
            Neues Turnier
          </Button>
        </Stack>

        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          {isLoading && <Typography variant="body2">Laden…</Typography>}
          {isError && (
            <Typography color="error">
              Fehler beim Laden.{" "}
              <Button size="small" onClick={refetch}>
                Erneut laden
              </Button>
            </Typography>
          )}
          {isDeleting && <Typography variant="body2">Lösche…</Typography>}
        </Box>

        <Grid container spacing={2}>
          {sortedTournaments.map((tournament) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={tournament._id || tournament.id}>
              <TournamentCard
                tournament={tournament}
                onEdit={() => handleEdit(tournament)}
                onDelete={() => handleDelete(tournament)}
              />
            </Grid>
          ))}

          {!isLoading && sortedTournaments.length === 0 && (
            <Grid item xs={12}>
              <EmptyState onCreate={handleCreate} />
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <TournamentFormDialog
          initialTournament={editTournament}
          onClose={() => setOpenForm(false)}
          onSaved={handleAfterSave}
        />
      </Dialog>
    </>
  );
}

const TournamentCard = ({ tournament, onEdit, onDelete }) => {
  const dateLabel = tournament?.tournamentDate
    ? dayjs(tournament.tournamentDate).format("DD.MM.YYYY")
    : "Kein Datum";

  const disciplines = Array.isArray(tournament?.disciplines)
    ? tournament.disciplines
    : [];

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        background:
          "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,248,248,1) 100%)",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 14px 34px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h6">
            {tournament.tournamentName || "Ohne Titel"}
          </Typography>
          <Chip
            size="small"
            label={disciplines.length ? `${disciplines.length} Disziplinen` : "Keine"}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {dateLabel}
        </Typography>
        {tournament.locationName && (
          <Typography variant="body2" color="text.secondary">
            {tournament.locationName}
          </Typography>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Disziplinen
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {disciplines.length === 0 && (
            <Chip size="small" label="Keine Disziplinen" />
          )}
          {disciplines.map((d, idx) => (
            <Chip
              key={`${d.name}-${idx}`}
              size="small"
              label={d.name}
              sx={{
                backgroundColor: "rgba(0,0,0,0.04)",
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={onEdit}
            startIcon={<Iconify icon="mdi:pencil" />}
          >
            Bearbeiten
          </Button>
          <TournamentPdfButton tournament={tournament} fullWidth />
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={onDelete}
            startIcon={<Iconify icon="mdi:trash-outline" />}
          >
            Löschen
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
};

const EmptyState = ({ onCreate }) => (
  <Box
    sx={{
      p: 4,
      border: "1px dashed rgba(0,0,0,0.2)",
      borderRadius: 2,
      textAlign: "center",
    }}
  >
    <Typography variant="h6" sx={{ mb: 1 }}>
      Noch keine Turniere
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Erstelle dein erstes Turnier und verwalte Disziplinen und Ergebnisse.
    </Typography>
    <Button
      variant="contained"
      color="secondary"
      onClick={onCreate}
      startIcon={<Iconify icon="mdi:plus" />}
    >
      Turnier erstellen
    </Button>
  </Box>
);
